/**
 * Encode/decode text fields for AI services config so that HTML/Unicode is not sent
 * as raw text in API request bodies. This avoids Cloud Armor XSS WAF rules
 * (OWASP CRS) from blocking legitimate config save/load requests while still
 * allowing HTML to be stored and rendered in the Chrome extension.
 */
import type { AiServicesConfig } from 'Components/organisms/AiServiceConfigurations/AiServiceConfigurationModal';

const STATUSES = ['APPROVED', 'DISAPPROVED', 'UNDEFINED'] as const;

function encodeStringForTransport(str: string): string {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }
  try {
    const bytes = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    );
    return btoa(bytes);
  } catch {
    return str;
  }
}

function decodeStringFromTransport(encoded: string): string {
  if (typeof encoded !== 'string' || encoded.length === 0) {
    return encoded;
  }
  try {
    const bytes = atob(encoded);
    return decodeURIComponent(
      bytes
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    return encoded;
  }
}

/** Returns true if a string looks like base64 (no HTML/angle brackets). */
function looksLikeBase64(s: string): boolean {
  return /^[A-Za-z0-9+/=]+$/.test(s) && s.length >= 4 && !s.includes('<') && !s.includes('>');
}

function encodeTemplate(template: any) {
  if (template.bestPracticesHtml) {
    template.bestPracticesHtml = encodeStringForTransport(template.bestPracticesHtml);
  }
  if (template.limpModeText) {
    template.limpModeText = encodeStringForTransport(template.limpModeText);
  }
  if (template.placeholders) {
    for (const key of Object.keys(template.placeholders)) {
      if (template.placeholders?.[key]) {
        template.placeholders[key] = encodeStringForTransport(template.placeholders[key]);
      }
    }
  }
}

function decodeTemplate(template: any) {
  if (template.bestPracticesHtml && looksLikeBase64(template.bestPracticesHtml)) {
    template.bestPracticesHtml = decodeStringFromTransport(template.bestPracticesHtml);
  }
  if (template.limpModeText && looksLikeBase64(template.limpModeText)) {
    template.limpModeText = decodeStringFromTransport(template.limpModeText);
  }
  if (template.placeholders) {
    for (const key of Object.keys(template.placeholders)) {
      const value = template.placeholders?.[key];
      if (value && looksLikeBase64(value)) {
        template.placeholders[key] = decodeStringFromTransport(value);
      }
    }
  }
}

/**
 * Encode all text fields (bestPracticesHtml, limpModeText, placeholders) in the config for API transport.
 * Call this before sending config to the API (e.g. updateSettings).
 */
export function encodeAiServicesConfigForApi(config: AiServicesConfig): AiServicesConfig {
  const encoded = JSON.parse(JSON.stringify(config)) as AiServicesConfig;
  for (const service of encoded.services ?? []) {
    for (const status of STATUSES) {
      const template = service.templates?.[status];
      if (template) {
        encodeTemplate(template);
      }
    }
  }
  return encoded;
}

/**
 * Decode all text fields (bestPracticesHtml, limpModeText, placeholders) from API response.
 * Call this when reading config from tenant settings (e.g. getAiServicesConfig).
 */
export function decodeAiServicesConfigFromApi(config: AiServicesConfig | null | undefined): AiServicesConfig | null {
  if (!config?.services) {
    return config ?? null;
  }
  const decoded = JSON.parse(JSON.stringify(config)) as AiServicesConfig;
  for (const service of decoded.services) {
    for (const status of STATUSES) {
      const template = service.templates?.[status];
      if (template) {
        decodeTemplate(template);
      }
    }
  }
  return decoded;
}
